import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { shareWhatsApp } from '../utils/whatsapp';
import { useAuth } from '../utils/AuthContext';
import { useLang } from '../utils/LanguageContext';
import { t } from '../utils/translations';
import Loader from '../components/Loader';
import './ReservationsScreen.css';

const EMPTY = { event_date: '', client_name: '', client_phone: '', price: '', deposit: '', status: 'reserved', notes: '' };

function ReservationsScreen() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { org, role } = useAuth();
  const { lang } = useLang();
  const tr = t[lang];
  const cur = org?.currency || 'MAD';
  const canDelete = role === 'owner' || role === 'manager';
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => { fetchList(); }, []);

  async function fetchList() {
    const { data } = await supabase
      .from('reservations')
      .select('*')
      .order('event_date', { ascending: true });
    setList(data || []);
    setLoading(false);
  }

  const money = n => `${Number(n || 0).toLocaleString('fr-FR')} ${cur}`;
  const remaining = r => Math.max(0, Number(r.price || 0) - Number(r.deposit || 0));

  // Upcoming = reserved and not in the past
  const upcoming = list.filter(r => r.status === 'reserved' && r.event_date >= today);
  const totalBooked = upcoming.reduce((s, r) => s + Number(r.price || 0), 0);
  const totalDeposits = upcoming.reduce((s, r) => s + Number(r.deposit || 0), 0);
  const totalRemaining = upcoming.reduce((s, r) => s + remaining(r), 0);

  function openAdd() {
    setEditingId(null);
    setForm({ ...EMPTY, event_date: today });
    setError('');
    setShowForm(true);
  }

  function openEdit(r) {
    setEditingId(r.id);
    setForm({
      event_date: r.event_date,
      client_name: r.client_name || '',
      client_phone: r.client_phone || '',
      price: r.price ?? '',
      deposit: r.deposit ?? '',
      status: r.status,
      notes: r.notes || '',
    });
    setError('');
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY);
    setError('');
  }

  async function save(e) {
    e.preventDefault();
    setError('');
    if (!form.event_date || !form.client_name.trim()) {
      setError(tr.resvMissing);
      return;
    }
    if (Number(form.deposit || 0) > Number(form.price || 0)) {
      setError(tr.resvDepositTooHigh);
      return;
    }

    // One booking per day: warn if that day is already taken (soft — can force)
    const clash = list.find(r =>
      r.event_date === form.event_date &&
      r.status !== 'cancelled' &&
      r.id !== editingId
    );
    if (clash && !window.confirm(tr.resvDayTaken.replace('{client}', clash.client_name))) {
      return;
    }

    setSaving(true);
    const payload = {
      event_date: form.event_date,
      client_name: form.client_name.trim(),
      client_phone: form.client_phone.trim() || null,
      price: Number(form.price || 0),
      deposit: Number(form.deposit || 0),
      status: form.status,
      notes: form.notes.trim() || null,
    };

    const { error: err } = editingId
      ? await supabase.from('reservations').update(payload).eq('id', editingId)
      : await supabase.from('reservations').insert(payload);

    if (err) {
      setError(tr.signUpError);
      setSaving(false);
      return;
    }
    setSaving(false);
    closeForm();
    fetchList();
  }

  async function del() {
    if (!editingId) return;
    if (!window.confirm(tr.resvDeleteConfirm)) return;
    await supabase.from('reservations').delete().eq('id', editingId);
    closeForm();
    fetchList();
  }

  function receipt(r) {
    const dateStr = new Date(r.event_date).toLocaleDateString(lang === 'ar' ? 'ar-MA' : 'fr-MA', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
    const msg =
      `🌅 ${org?.name || ''}\n` +
      `${tr.resvReceiptTitle}\n\n` +
      `${tr.clientName}: ${r.client_name}\n` +
      `${tr.eventDate}: ${dateStr}\n` +
      `${tr.rentalPrice}: ${money(r.price)}\n` +
      `${tr.deposit}: ${money(r.deposit)}\n` +
      `${tr.remaining}: ${money(remaining(r))}\n\n` +
      `${tr.receiptThanks}`;
    shareWhatsApp(msg, r.client_phone);
  }

  const statusLabel = { reserved: tr.statusReserved, done: tr.statusDone, cancelled: tr.statusCancelled };

  if (loading) return <Loader text={tr.loadingApp} />;

  return (
    <div className="resv-screen">
      <div className="resv-head">
        <h2 className="resv-title">💒 {tr.reservations}</h2>
        <button className="resv-add-btn" onClick={openAdd}>+ {tr.newReservation}</button>
      </div>

      {/* Summary of what's coming */}
      <div className="resv-summary">
        <div className="resv-stat">
          <span className="resv-stat-val">{money(totalBooked)}</span>
          <span className="resv-stat-lbl">{tr.totalBooked}</span>
        </div>
        <div className="resv-stat">
          <span className="resv-stat-val">{money(totalDeposits)}</span>
          <span className="resv-stat-lbl">{tr.depositsReceived}</span>
        </div>
        <div className="resv-stat resv-stat-due">
          <span className="resv-stat-val">{money(totalRemaining)}</span>
          <span className="resv-stat-lbl">{tr.toCollect}</span>
        </div>
      </div>

      {showForm && (
        <form className="resv-form" onSubmit={save}>
          <h3>{editingId ? tr.editReservation : tr.newReservation}</h3>
          <label className="resv-label">{tr.eventDate}</label>
          <input type="date" className="resv-input" value={form.event_date}
            onChange={e => setForm({ ...form, event_date: e.target.value })} required />

          <label className="resv-label">{tr.clientName}</label>
          <input type="text" className="resv-input" value={form.client_name}
            onChange={e => setForm({ ...form, client_name: e.target.value })} required />

          <label className="resv-label">{tr.clientPhone}</label>
          <input type="tel" className="resv-input" value={form.client_phone}
            onChange={e => setForm({ ...form, client_phone: e.target.value })} placeholder="06…" />

          <div className="resv-row2">
            <div>
              <label className="resv-label">{tr.rentalPrice}</label>
              <input type="number" min="0" step="any" className="resv-input" value={form.price}
                onChange={e => setForm({ ...form, price: e.target.value })} placeholder="2800" />
            </div>
            <div>
              <label className="resv-label">{tr.deposit}</label>
              <input type="number" min="0" step="any" className="resv-input" value={form.deposit}
                onChange={e => setForm({ ...form, deposit: e.target.value })} placeholder="1000" />
            </div>
          </div>

          {form.price !== '' && (
            <p className="resv-remaining-hint">
              {tr.remaining}: <strong>{money(Math.max(0, Number(form.price || 0) - Number(form.deposit || 0)))}</strong>
            </p>
          )}

          <label className="resv-label">{tr.statusLabel}</label>
          <select className="resv-input" value={form.status}
            onChange={e => setForm({ ...form, status: e.target.value })}>
            <option value="reserved">{tr.statusReserved}</option>
            <option value="done">{tr.statusDone}</option>
            <option value="cancelled">{tr.statusCancelled}</option>
          </select>

          <label className="resv-label">{tr.resvNotes}</label>
          <textarea className="resv-input" rows={2} value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })} />

          {error && <p className="resv-error">❌ {error}</p>}

          <div className="resv-form-actions">
            <button type="button" className="resv-cancel" onClick={closeForm}>{tr.cancel}</button>
            <button type="submit" className="resv-save" disabled={saving}>
              {saving ? '...' : tr.save}
            </button>
          </div>
          {editingId && canDelete && (
            <button type="button" className="resv-delete" onClick={del}>🗑️ {tr.resvDelete}</button>
          )}
        </form>
      )}

      {/* The list — the digital carnet */}
      {list.length === 0 ? (
        <p className="resv-empty">{tr.noReservations}</p>
      ) : (
        <div className="resv-list">
          {list.map(r => (
            <div key={r.id} className={`resv-card status-${r.status} ${r.event_date < today && r.status === 'reserved' ? 'past' : ''}`}>
              <div className="resv-card-main" onClick={() => openEdit(r)}>
                <div className="resv-card-date">
                  {new Date(r.event_date).toLocaleDateString(lang === 'ar' ? 'ar-MA' : 'fr-MA',
                    { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
                <div className="resv-card-client">{r.client_name}</div>
                <div className="resv-card-money">
                  <span>{money(r.price)}</span>
                  {remaining(r) > 0 && <span className="resv-card-due">{tr.remaining}: {money(remaining(r))}</span>}
                </div>
              </div>
              <div className="resv-card-side">
                <span className={`resv-badge badge-${r.status}`}>{statusLabel[r.status]}</span>
                <button className="resv-receipt-btn" onClick={() => receipt(r)} title={tr.receiptResv}>🧾</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ReservationsScreen;
